/* eslint-disable max-lines */
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { getPropertyFromSchemaPath } from '@app/components/form/services/schema-form/schema-form-functions';
import {
  FieldList,
  IFieldPreferences,
  IFormGroupPreferences,
  IFormRules,
} from '@app/components/form/services/schema-form/schema-form-interfaces';
import { widgetOption } from '@app/components/form/services/schema-form/widget-options';
import { lazyInject } from '@app/containers/base/decorators';
import { isObservable, Observable, rx } from '@app/shared/rx/rx-wrapper';
import { CELL_RENDERER } from '@app/shared/types/enums/cell-renderer.enum';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import { IModalDialogOptions } from '@app/shared/types/ui/modal-options.type';
import {
  IToolbarButtonDefinition,
  IXVIListColumn,
} from '@app/shared/types/ui/xux-grid/xux-grid.type';
import {
  SchemaNode,
  SelectOption,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  chain,
  compact,
  concat,
  each,
  flow,
  forEach,
  get,
  has,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isObject,
  isPlainObject,
  isString,
  keys,
  map,
  mapValues,
  merge,
  omit,
  reduce,
  set,
  startCase,
  trim,
  uniq,
  unset,
} from 'lodash';
import { FilterOptionsMappers } from '../constants/filter-options-mappers.constant';
import { FORM_WIDGET_STATIC_TEXT_CONTENT } from '../constants/form-widget.staticTextContent.constants';
import { GRID_FILTERS_STATIC_TEXT_CONTENT } from '../constants/gird-filter.staticTextContent.constants';
import { GRID_RENDERERS_STATIC_TEXT_CONTENT } from '../constants/gird-render.staticTextContent.constants';
import {
  ComponentStaticText,
  IPreparePathsForArray,
  IPreparePathsForSimpleField,
  UserDefinedTranslationApplyingOptions,
  UserDefinedTranslationRevertOptions,
} from '../i18n.types';
import { I18n } from '../strings-keys';
import {
  FieldTransformer,
  FieldTransformerConfig,
} from '../utils/translate-utils.types';
import {
  translateFieldsInObj,
  translateSelectOptions,
} from '../utils/translate.utils';
import {
  IBaseEntityType,
  II18nConfig,
  ServiceIdentifyType,
} from '@app/shared/types';
import { cloneDataWithDepth } from '@app/shared/utils';

import {
  customEntityTypeMapper,
  getUserDefineStringPaths,
  getUserDefinedConfig,
} from '@i18n/user-defined-strings.helper';
import {
  ARRAY_OF_OBJECT_PATH_SEPARATOR,
  DEFAULT_LOCALE,
  MAX_CLONE_DEPTH,
  NESTED_FIELD_SEPARATOR,
  ORIGINAL_VALUE_FIELD,
  TRANSLATION_OBJECT_FIELD,
} from '@config/global-constants';
import { StateService } from '@shared/services/state-service';
import { I18nUserDefinedConfig } from '../i18n-user-defined.types';
import { BaseEntityType } from '@geniuspos/xenial-data-contracts/shared/dm/base.type';

@Injectable()
export class XVII18nService {
  @lazyInject(TranslateService)
  public translateService: TranslateService;

  @lazyInject(StateService)
  public stateService: StateService;

  public constructor(protected injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public instant(key: string | Array<string>, interpolateParams?: Object) {
    if (isNil(key) || !isString(key)) {
      return key;
    }

    return this.translateService.instant.apply(this.translateService, [
      key,
      interpolateParams,
    ]);
  }

  /**
   * alias of instant method
   */
  public translate = this.instant;

  /**
   * alias of instant method
   */
  public t = this.instant;

  public getFieldTransformer(
    path: string,
    defaultValue: string,
    forciblySet: boolean = true,
    skipTransform?: Function,
  ): FieldTransformer {
    const fieldTransformer = {
      path,
      forciblySet,
      mappers: {
        str: (value: string): string => this.instant(value || defaultValue),
      },
    };

    if (skipTransform) set(fieldTransformer, 'skipTransform', skipTransform);

    return fieldTransformer;
  }

  public translateHeaderNames(headerNames: ColDef[]): ColDef[] {
    return map(headerNames, (item) => ({
      ...item,
      headerName: this.instant(item?.headerName),
    }));
  }

  public translateColumnsFilter(columns: IXVIListColumn[]) {
    const options = {
      translateService: this.translateService,
    };

    return map(columns, (column) => ({
      ...column,
      filterParams: {
        ...omit(column.filterParams || {}, 'staticTextContent'),
        i18n: this.translateStaticTextContent.bind(this)(
          column.filterParams?.staticTextContent ||
            get(GRID_FILTERS_STATIC_TEXT_CONTENT, column.filterType, {}),
        ),
        filterOptionsMapper: (selectOptions) => {
          if (isFunction(column.filterParams?.filterOptionsMapper)) {
            return column.filterParams.filterOptionsMapper(
              selectOptions,
              options,
            );
          }

          const defaultFilterOptionsMapper = get(
            FilterOptionsMappers,
            column.filterType,
            null,
          );

          const mappedSelectOptions = isFunction(defaultFilterOptionsMapper)
            ? defaultFilterOptionsMapper(selectOptions, options)
            : selectOptions;

          return translateSelectOptions(mappedSelectOptions, options);
        },
      },
    }));
  }

  public translateRenderersInColumns(
    columns: IXVIListColumn[],
  ): IXVIListColumn[] {
    return map(columns, (column) => ({
      ...column,
      cellRendererParams: isFunction(column.cellRendererParams)
        ? (params: ICellRendererParams) => {
            const colRenderParams = column.cellRendererParams || {};

            const colRenderParamsValue = (colRenderParams as Function)(params);

            return {
              ...omit(colRenderParamsValue || {}, 'staticTextContent'),
              i18n: this.translateStaticTextContent.bind(this)(
                colRenderParamsValue?.staticTextContent ||
                  get(
                    GRID_RENDERERS_STATIC_TEXT_CONTENT,
                    column.cellRenderer as string,
                    {},
                  ),
              ),
            };
          }
        : {
            ...omit(column.cellRendererParams || {}, 'staticTextContent'),
            i18n: this.translateStaticTextContent.bind(this)(
              column.cellRendererParams?.staticTextContent ||
                get(
                  GRID_RENDERERS_STATIC_TEXT_CONTENT,
                  column.cellRenderer as string,
                  {},
                ),
            ),
          },
    }));
  }

  public translateGridComponentsInColumns(
    columns: IXVIListColumn[],
  ): IXVIListColumn[] {
    /* eslint-disable-next-line  */
    return flow(
      this.translateColumnsFilter.bind(this),
      this.translateRenderersInColumns.bind(this),
    )(columns) as IXVIListColumn[];
  }

  public translateTextInColumns(columns: IXVIListColumn[]): IXVIListColumn[] {
    const listOfSwitchCellRenderers = [];
    const fieldsPathsToTranslate: FieldTransformerConfig = {
      paths: [{ path: 'title' }],
      nestedFields: [
        {
          parentField: 'pinnedRowCellRendererParams',
          paths: [
            this.getFieldTransformer.bind(this)(
              'trueLabel',
              I18n.Common.Yes,
              true,
              this.skipTransformMapper(
                'pinnedRowCellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            this.getFieldTransformer.bind(this)(
              'falseLabel',
              I18n.Common.No,
              true,
              this.skipTransformMapper(
                'pinnedRowCellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            this.getFieldTransformer.bind(this)(
              'indeterminateText',
              I18n.Components.SwitchWidget.Mixed,
              true,
              this.skipTransformMapper(
                'pinnedRowCellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            { path: 'placeholder' },
          ],
        },
        {
          parentField: 'headerComponentParams',
          paths: [],
          nestedFields: [
            {
              parentField: 'hintOptions',
              paths: [{ path: 'description' }],
            },
          ],
        },
        {
          parentField: 'cellRendererParams',
          paths: [
            this.getFieldTransformer.bind(this)(
              'trueLabel',
              I18n.Common.Yes,
              true,
              this.skipTransformMapper(
                'cellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            this.getFieldTransformer.bind(this)(
              'falseLabel',
              I18n.Common.No,
              true,
              this.skipTransformMapper(
                'cellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            this.getFieldTransformer.bind(this)(
              'indeterminateText',
              I18n.Components.SwitchWidget.Mixed,
              true,
              this.skipTransformMapper(
                'cellRenderer',
                listOfSwitchCellRenderers,
              ),
            ),
            { path: 'emptyOptionText' },
            { path: 'placeholder' },
            {
              path: 'options',
              mappers: {
                arr: (selectOptions) =>
                  translateSelectOptions(selectOptions, {
                    translateService: this.translateService,
                  }),
              },
            },
            { path: 'title' },
          ],
        },
        {
          parentField: 'filterParams',
          paths: [
            {
              path: 'values',
              mappers: {
                arr: (selectOptions) => {
                  return translateSelectOptions(selectOptions, {
                    translateService: this.translateService,
                  });
                },
                behaviorSubject: (selectOptions) => {
                  return isArray(selectOptions) && !isEmpty(selectOptions)
                    ? translateSelectOptions(selectOptions, {
                        translateService: this.translateService,
                      })
                    : selectOptions;
                },
              },
            },
          ],
        },
      ],
    };

    const translatedGridComponentsInColumns =
      this.translateGridComponentsInColumns(columns);

    return map(translatedGridComponentsInColumns, (column) =>
      translateFieldsInObj({
        fieldsPathsToTranslate,
        obj: column,
        transformer: this.instant.bind(this),
      }),
    );
  }

  public translateFieldsInFormRules(
    fields: FieldList,
    fieldsPathsToTranslate: FieldTransformerConfig,
    scheme?: SchemaNode,
  ): FieldList {
    return reduce(
      keys(fields),
      (acc, fieldName) => {
        const field = { ...fields[fieldName] };

        /**
         * special logic for nested virtual fields in the fields
         */
        if (isEqual(fieldName, 'virtual') && !isEmpty(field)) {
          return {
            ...acc,
            [fieldName]: this.translateFieldsInFormRules(
              field as FieldList,
              fieldsPathsToTranslate,
              scheme,
            ),
          };
        }
        const name: string = fieldName.split('.').pop();
        if (!field.title && scheme) {
          const fieldPath = getPropertyFromSchemaPath(scheme, name);
          const fieldInScheme = get(scheme, fieldPath, null);
          if (has(fieldInScheme, 'title')) field.title = fieldInScheme.title;
        }
        const { autoTitle } = field.ignoreAutoTitleWhenTranslate
          ? { autoTitle: false }
          : widgetOption(get(field, 'widget', null));
        if (!field.title && autoTitle && !isString(field.title)) {
          field.title = startCase(name);
        }
        const newFiledValue = translateFieldsInObj({
          fieldsPathsToTranslate,
          obj: field,
          transformer: this.translateService.instant.bind(
            this.translateService,
          ),
        });

        return {
          ...acc,
          [fieldName]: newFiledValue,
        };
      },
      {},
    );
  }

  public translateTextInFormRules(
    formRules: IFormRules,
    scheme?: SchemaNode,
  ): IFormRules {
    const fieldsPathsToTranslate: FieldTransformerConfig =
      this.getFieldsPathsToTranslate();

    const transformedGroups = map(formRules.groups, (group) =>
      translateFieldsInObj({
        obj: group,
        fieldsPathsToTranslate: { paths: [{ path: 'name' }] },
        transformer: this.instant.bind(this),
      }),
    );
    const transformedFormRules = { ...formRules, groups: transformedGroups };

    if (!isEmpty(formRules.fields)) {
      const patchedFields = this.fieldsMapper(formRules.fields, scheme);

      const transformedFields = this.translateFieldsInFormRules(
        patchedFields,
        fieldsPathsToTranslate,
        scheme,
      );
      set(transformedFormRules, 'fields', transformedFields);
    }

    if (!isEmpty(formRules?.bannerText)) {
      const translatedBannerText = this.instant(formRules.bannerText);
      set(transformedFormRules, 'bannerText', translatedBannerText);
    }

    if (!isEmpty(formRules.virtual)) {
      const transformedFields = this.translateFieldsInFormRules(
        formRules.virtual.properties,
        fieldsPathsToTranslate,
      );
      set(transformedFormRules, 'virtual.properties', transformedFields);
    }

    return transformedFormRules;
  }

  public translatePropertiesInScheme(
    properties: Record<string, SchemaNode>,
    fieldsPathsToTranslate: FieldTransformerConfig,
  ): Record<string, SchemaNode> {
    return reduce(
      keys(properties),
      (acc, propertyName) => {
        const newPropertyValue = translateFieldsInObj({
          fieldsPathsToTranslate,
          obj: properties[propertyName],
          transformer: this.instant.bind(this),
        });
        if (!isEmpty(properties[propertyName].properties)) {
          set(
            newPropertyValue,
            'properties',
            this.translatePropertiesInScheme(
              properties[propertyName].properties,
              fieldsPathsToTranslate,
            ),
          );
        }

        return set(acc, propertyName, newPropertyValue);
      },
      {},
    );
  }

  public translateTextInSchema(schema: SchemaNode): SchemaNode {
    if (!schema) return;

    const fieldsPathsToTranslate: FieldTransformerConfig = {
      paths: [{ path: 'description' }],
    };

    return {
      ...schema,
      properties: this.translatePropertiesInScheme(
        schema.properties,
        fieldsPathsToTranslate,
      ),
    };
  }

  public translateTextInFormGroups(
    groups: IFormGroupPreferences[],
    params: {
      /**
       * is need keep original object reference if the object is not changed after translation.
       * It can be useful for performance optimization in case of big forms with many fields,
       * but should be used with caution as it can lead to unexpected behavior in some cases.
       */
      keepReference?: boolean;
    },
  ): IFormGroupPreferences[] {
    const { keepReference = false } = params;
    const fieldsPathsToTranslate: FieldTransformerConfig =
      this.getFieldsPathsToTranslate();

    return map(groups, (group) => {
      const transformedGroup = translateFieldsInObj({
        fieldsPathsToTranslate,
        obj: group,
        transformer: this.translateService.instant.bind(this.translateService),
      });

      if (!isEmpty(group.fields)) {
        const transformedFields = map(group.fields, (field) =>
          translateFieldsInObj({
            keepReference,
            fieldsPathsToTranslate,
            obj: field,
            transformer: this.translateService.instant.bind(
              this.translateService,
            ),
          }),
        );
        set(transformedGroup, 'fields', transformedFields);
      }

      return transformedGroup;
    });
  }

  public translateTextInModalDialogOptions(
    modalDialogOptions: IModalDialogOptions,
  ): IModalDialogOptions {
    const fieldsPathsToTranslate: FieldTransformerConfig = {
      paths: [
        { path: 'title' },
        { path: 'titleObs' },
        { path: 'subTitle' },
        { path: 'subTitleObs' },
        { path: 'text' },
        { path: 'label' },
      ],
    };
    const transformedModal = {
      ...translateFieldsInObj({
        fieldsPathsToTranslate,
        obj: modalDialogOptions,
        transformer: this.instant.bind(this),
      }),
    };

    if (modalDialogOptions.formRules) {
      const transformedFormRules = this.translateTextInFormRules(
        modalDialogOptions.formRules,
      );
      set(transformedModal, 'formRules', transformedFormRules);
    }

    if (modalDialogOptions.buttons) {
      const transformedButtons = this.translateButtonsText(
        modalDialogOptions.buttons,
      );
      set(transformedModal, 'buttons', transformedButtons);
    }

    if (modalDialogOptions.schema) {
      const transformedSchema = isObservable(modalDialogOptions.schema)
        ? modalDialogOptions.schema.pipe(
            rx.map(this.translateTextInSchema.bind(this)),
          )
        : this.translateTextInSchema(modalDialogOptions.schema);
      set(transformedModal, 'schema', transformedSchema);
    }

    return transformedModal;
  }

  public translateButtonText(
    button: IToolbarButtonDefinition,
  ): IToolbarButtonDefinition {
    const updatedButton = { ...button };
    if (button.childItems) {
      set(
        updatedButton,
        'childItems',
        this.translateButtonsText(button.childItems),
      );
    }
    if (isString(button.caption) && !button.omitTranslation) {
      /* eslint-disable-next-line  */
      set(updatedButton, 'caption', this.instant(button.caption));
    }
    if (isString(button.disableTooltip) && !button.omitTranslation) {
      /* eslint-disable-next-line  */
      set(updatedButton, 'disableTooltip', this.instant(button.disableTooltip));
    }

    return updatedButton;
  }

  public translateButtonsText(
    buttons: IToolbarButtonDefinition[],
  ): IToolbarButtonDefinition[] {
    return map(buttons, this.translateButtonText.bind(this));
  }

  public translateStaticTextContent(
    staticTextContent: ComponentStaticText,
  ): ComponentStaticText {
    const translatedStaticTextContent: ComponentStaticText = {};

    each(keys(staticTextContent), (key) => {
      translatedStaticTextContent[key] = isObject(staticTextContent[key])
        ? /* eslint-disable-next-line  */
          this.translateStaticTextContent.bind(this)(
            staticTextContent[key] as ComponentStaticText,
          )
        : this.translateService.instant.bind(this.translateService)(
            staticTextContent[key],
          );
    });

    return translatedStaticTextContent;
  }

  public translateArrayLikeText(values: string | string[]): string | string[] {
    if (isNil(values)) {
      return values;
    }

    return map(isArray(values) ? values : [values], (value) =>
      this.instant(value),
    );
  }

  protected patchSwitchWidgetFields(
    fields: FieldList<string>,
  ): FieldList<string> {
    return mapValues(fields, (field) => {
      if (field?.widget === WIDGET_COMPONENT.switch) {
        const updatedField: IFieldPreferences = {
          ...field,
          widgetData: {
            ...(field.widgetData || {}),
          },
        };

        if (!field.widgetData?.checkedText) {
          updatedField.widgetData = {
            ...updatedField.widgetData,
            checkedText: I18n.Components.SwitchWidget.On,
          };
        }

        if (!field.widgetData?.uncheckedText) {
          updatedField.widgetData = {
            ...updatedField.widgetData,
            uncheckedText: I18n.Components.SwitchWidget.Off,
          };
        }

        return updatedField;
      }

      return field;
    });
  }

  protected replaceEnumBySelectOptions(
    scheme: SchemaNode,
    fields: FieldList<string>,
  ): FieldList<string> {
    return mapValues(fields, (field, key) => {
      if (
        !field ||
        (has(field, 'omitTranslationProperties') &&
          includes(field.omitTranslationProperties, 'selectOptions'))
      ) {
        return field;
      }

      if (field.enum && !field.selectOptions) {
        const selectOptions = map(field.enum, (item) => ({
          value: item,
          text: get(field, ['optionText', item]) || startCase(item),
        }));

        return {
          ...field,
          selectOptions,
        };
      }

      const schemaPath = getPropertyFromSchemaPath(scheme, key);
      const propertyInSchema = get(scheme, schemaPath);
      if (
        !field.enum &&
        !field.selectOptions &&
        (propertyInSchema?.enum || propertyInSchema?.items?.enum)
      ) {
        const selectOptions = map(
          propertyInSchema.enum || propertyInSchema?.items?.enum,
          (item) => ({
            value: propertyInSchema.type === 'integer' ? +item : item,
            text: get(field, ['optionText', item]) || startCase(item),
          }),
        );

        return {
          ...field,
          selectOptions,
        };
      }

      if (!field.enum && !field.selectOptions && field.optionText) {
        const selectOptions = map(field.optionText, (item, optionKey) => ({
          value: optionKey,
          text: item,
        }));

        return {
          ...field,
          selectOptions,
        };
      }

      return field;
    });
  }

  /**
   * translate static text content for fields
   * translated content will be set to `widgetData.i18n`
   *
   * @param scheme
   * @param fields
   * @returns
   */
  protected translateFieldsStaticTextContent(
    scheme: SchemaNode,
    fields: FieldList<string>,
  ): FieldList<string> {
    return mapValues(fields, (field) => {
      const defaultWidgetStaticTextContent = get(
        FORM_WIDGET_STATIC_TEXT_CONTENT,
        field.widget as string,
        {},
      );
      const widgetStaticTextContent = get(
        field,
        'widgetData.staticTextContent',
        defaultWidgetStaticTextContent,
      );

      if (widgetStaticTextContent) {
        const i18n = this.translateStaticTextContent(widgetStaticTextContent);

        return {
          ...field,
          widgetData: {
            ...field.widgetData,
            i18n,
          },
        };
      }

      return field;
    });
  }

  protected fieldsMapper(
    fields: FieldList<string>,
    scheme?: SchemaNode,
  ): FieldList<string> {
    /* eslint-disable-next-line */
    return flow(
      this.patchSwitchWidgetFields.bind(this),
      this.replaceEnumBySelectOptions.bind(this, scheme),
      this.translateFieldsStaticTextContent.bind(this, scheme),
    )(fields) as FieldList<string>;
  }

  protected skipTransformMapper =
    (
      propertyPath: string,
      widgetType: WIDGET_COMPONENT[] | CELL_RENDERER[],
    ): Function =>
    (field: IFieldPreferences, originalField?: IFieldPreferences): boolean => {
      const propertyValue =
        field[propertyPath] ?? originalField[propertyPath] ?? '';

      return !includes(widgetType, propertyValue);
    };

  protected getFieldsPathsToTranslate(): FieldTransformerConfig {
    return {
      paths: [
        { path: 'title' },
        { path: 'titleObs' },
        { path: 'description' },
        { path: 'descriptionObs' },
        { path: 'placeholder' },
        { path: 'text' },
        {
          path: 'selectOptions',
          mappers: {
            arr: (selectOptions) =>
              translateSelectOptions(selectOptions, {
                translateService: this.translateService,
              }),
            behaviorSubject: (selectOptions) =>
              translateSelectOptions(selectOptions, {
                translateService: this.translateService,
              }),
            observable: (
              fieldValue: Observable<SelectOption[]>,
            ): Observable<SelectOption[]> =>
              fieldValue.pipe(
                rx.map((value: SelectOption[]) =>
                  map(value, (selectOption) =>
                    set(selectOption, 'text', this.instant(selectOption?.text)),
                  ),
                ),
              ),
          },
        },
      ],
      nestedFields: [
        {
          parentField: 'widgetData',
          paths: [
            { path: 'placeholder' },
            { path: 'modalTitle' },
            { path: 'searchPlaceholder' },
            { path: 'inlineAddButtonCaption' },
            { path: 'emptyOptionText' },
            { path: 'previewTitle' },
            {
              path: 'columns',
              mappers: {
                arr: this.translateTextInColumns.bind(this),
              },
            },
            {
              path: 'buttons',
              mappers: {
                arr: this.translateButtonsText.bind(this),
              },
            },
            {
              path: 'headerNames',
              mappers: {
                arr: this.translateHeaderNames.bind(this),
              },
            },
            this.getFieldTransformer.bind(this)(
              'checkedText',
              I18n.Components.SwitchWidget.On,
              true,
              this.skipTransformMapper('widget', [WIDGET_COMPONENT.switch]),
            ),
            this.getFieldTransformer.bind(this)(
              'uncheckedText',
              I18n.Components.SwitchWidget.Off,
              true,
              this.skipTransformMapper('widget', [WIDGET_COMPONENT.switch]),
            ),
            this.getFieldTransformer.bind(this)(
              'indeterminateText',
              I18n.Components.SwitchWidget.Mixed,
              true,
              this.skipTransformMapper('widget', [WIDGET_COMPONENT.switch]),
            ),
            this.getFieldTransformer.bind(this)('itemName', '', false),
          ],
          nestedFields: [
            {
              parentField: 'momsProductsListWidgetData',
              paths: [{ path: 'description' }],
            },
            {
              parentField: 'userGridOptions',
              paths: [{ path: 'overlayNoRowsTemplate' }],
            },
            {
              parentField: 'inlineRemoveParams',
              paths: [{ path: 'disabledTooltip' }],
            },
            {
              parentField: 'panelButtonParams',
              paths: [{ path: 'caption' }],
            },
          ],
        },
      ],
      omitPropertiesPath: 'omitTranslationProperties',
    };
  }

  public applyUserDefinedTranslations<T extends any>(
    identify: ServiceIdentifyType,
    body: T,
    i18nConfig?: II18nConfig,
  ): T {
    if (has(body, 'items') && isArray((body as any).items)) {
      (body as any).items = map((body as any).items, (item) =>
        this.applyItemUserDefinedTranslations(identify, item, i18nConfig),
      );

      return body;
    }

    return this.applyItemUserDefinedTranslations(
      identify,
      body as any,
      i18nConfig,
    );
  }

  public applyItemUserDefinedTranslations<T extends object = IBaseEntityType>(
    identify: ServiceIdentifyType,
    doc: T,
    i18nConfig?: II18nConfig,
  ): T {
    const defaultI18nConfig = {
      isDocumentForEditor: false,
    };

    const mergedI18nConfig = merge({}, defaultI18nConfig, i18nConfig);

    const { isDocumentForEditor = false } = mergedI18nConfig;

    const { locale, paths } = this.getUserDefinedConfigToProcess(identify);

    if (!locale || isEmpty(paths)) return doc;

    const clonedData = cloneDataWithDepth(doc);
    const translatedDoc = reduce<string, T>(
      paths,
      (document: T, path: string): T => {
        try {
          const applyParams: UserDefinedTranslationApplyingOptions<T> = {
            document,
            locale,
            path,
            isDocumentForEditor,
            skipNonExistedFields: true,
          };

          return includes(path, ARRAY_OF_OBJECT_PATH_SEPARATOR)
            ? this.applyUserDefinedTranslationsForArrayOfObjects(applyParams)
            : this.applyUserDefinedTranslationsForSimpleField(applyParams);
        } catch (error) {
          console.error(`Failed to apply translation for path: ${path}`, error);

          return document; // Return document unchanged on error
        }
      },
      clonedData,
    );

    return translatedDoc;
  }

  public getUserDefinedConfigToProcess(
    identify: ServiceIdentifyType,
    i18nConfig?: II18nConfig,
  ): {
    paths?: string[];
    identify?: ServiceIdentifyType;
    locale?: string;
    userDefinedConfig?: I18nUserDefinedConfig;
  } {
    const { userLocale: locale } = this.stateService.getDictionaryLocals();

    if (!locale) return {};
    const mappedIdentify = customEntityTypeMapper(identify);

    return {
      locale,
      identify: mappedIdentify,
      paths: [...getUserDefineStringPaths(mappedIdentify, i18nConfig)],
      userDefinedConfig: getUserDefinedConfig(mappedIdentify, i18nConfig),
    };
  }

  public applyUserDefinedTranslationsForArrayOfObjects<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    isDocumentForEditor,
    skipNonExistedFields,
  }: UserDefinedTranslationApplyingOptions<T>): T {
    const {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
    } =
      this.preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(path);

    const clearedPathToArrayString = clearedPathToArray.join('.');
    const currentArrayValue = get(document, clearedPathToArray, []);
    if (isEmpty(currentArrayValue)) return document;
    const translatedArray = map(currentArrayValue, (currentObject, index) => {
      if (isEmpty(omit(currentObject, [TRANSLATION_OBJECT_FIELD]))) return null;

      if (!isEmpty(nestedPaths)) {
        const clearedNestedPaths = compact(
          nestedPaths.join(`.${ARRAY_OF_OBJECT_PATH_SEPARATOR}.`).split('.'),
        );
        const documentForRecursiveCall = get(document, [
          ...clearedPathToArray,
          `${index}`,
        ]);
        const newPathForRecursiveCall = [
          ...clearedNestedPaths,
          ARRAY_OF_OBJECT_PATH_SEPARATOR,
          ...clearedPathInObject,
        ].join('.');

        return this.applyUserDefinedTranslationsForArrayOfObjects({
          locale,
          isDocumentForEditor,
          document: documentForRecursiveCall,
          path: newPathForRecursiveCall,
        });
      }

      // just skip if no translation target field is provided
      if (skipNonExistedFields && !has(currentObject, clearedPathInObject)) {
        return currentObject;
      }

      const currentObjectValue = get(currentObject, clearedPathInObject, null);

      if (
        has(currentObject, pathToStoreOriginalFieldValue) &&
        !isDocumentForEditor
      ) {
        return currentObject;
      }

      const { pathToStoreWithLocale: pathToTranslatedValue } =
        this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
          clearedPathInObject.join('.'),
          locale,
        );

      const translatedObjectValue = this.getTranslationForObjectValue(
        currentObject,
        currentObjectValue,
        pathToStoreOriginalFieldValue,
        pathToTranslatedValue,
      );

      if (
        !isDocumentForEditor ||
        !has(currentObject, pathToStoreOriginalFieldValue)
      ) {
        set(
          currentObject,
          pathToStoreOriginalFieldValue,
          isEmpty(currentObjectValue) ? null : currentObjectValue,
        );
      }

      return set(currentObject, clearedPathInObject, translatedObjectValue);
    });

    const compactedArray = compact(translatedArray);

    if (compactedArray.length !== translatedArray.length) {
      if (compactedArray.length === 0) {
        unset(document, [clearedPathToArrayString]);

        return document;
      }
      if (isDocumentForEditor) {
        console.error(
          'Array size changes on translation. Some Elements were skipped due to emptiness',
        );
      }
    }

    return set(document, clearedPathToArray, translatedArray);
  }

  public applyUserDefinedTranslationsForSimpleField<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    isDocumentForEditor,
    skipNonExistedFields,
  }: UserDefinedTranslationApplyingOptions<T>): T {
    const {
      pathToField,
      fieldName,
      pathToStoreWithLocale: pathToTranslation,
    } = this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
      path,
      locale,
    );
    const pathToStoreOriginalFieldValue = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      ORIGINAL_VALUE_FIELD,
      fieldName,
    ]);

    const currentFieldValue = get(document, path, null);
    if (
      (skipNonExistedFields && !has(document, path)) ||
      (has(document, pathToStoreOriginalFieldValue) && !isDocumentForEditor)
    ) {
      return document;
    }

    let noTranslationLabel;
    if (isDocumentForEditor) {
      noTranslationLabel = '';
    } else {
      noTranslationLabel =
        this.buildNonTranslatedReadableValue(currentFieldValue);
    }
    const translatedValue = get(
      document,
      pathToTranslation,
      noTranslationLabel,
    );
    if (!has(document, pathToStoreOriginalFieldValue)) {
      set(
        document,
        pathToStoreOriginalFieldValue,
        isEmpty(currentFieldValue) ? null : currentFieldValue,
      );
    }

    return set(document, path, translatedValue);
  }

  public preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(
    originalPath: string,
    locale: string = null,
  ): IPreparePathsForArray {
    const { userLocale: defaultLocale } =
      this.stateService.getDictionaryLocals();

    const pathsToTheCurrentField = originalPath.split(
      `.${ARRAY_OF_OBJECT_PATH_SEPARATOR}.`,
    );
    const pathInObject = pathsToTheCurrentField.pop();
    const [closesDocumentInheritancePath, ...nestedPaths] =
      pathsToTheCurrentField;
    const clearedPathToArray = compact(
      closesDocumentInheritancePath.split('.'),
    );
    const clearedPathInObject = compact(pathInObject.split('.'));
    const {
      pathToField: pathToFieldInObject,
      pathToStoreWithLocale: pathToStoreOriginalFieldValue,
    } = this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
      pathInObject,
      ORIGINAL_VALUE_FIELD,
    );
    const { pathToStoreWithLocale } =
      this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
        pathInObject,
        locale ?? defaultLocale,
      );

    return {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
      pathToStoreWithLocale,
      pathToFieldInObject,
    };
  }

  public preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
    originalPath: string,
    locale?: string,
  ): IPreparePathsForSimpleField {
    const pathToField = compact([...originalPath.split('.')]);
    const fieldName = pathToField.pop();
    const pathToStoreWithLocale = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      locale,
      fieldName,
    ]);

    return {
      pathToField,
      fieldName,
      pathToStoreWithLocale,
    };
  }

  public getTranslationForObjectValue(
    obj: object,
    objValue: string,
    pathToOriginalValue: string[],
    pathToTranslatedValue: string[],
  ) {
    const isTranslationAlreadyApplied = has(obj, pathToOriginalValue);
    if (isTranslationAlreadyApplied) return objValue;

    const isTranslationPresent = has(obj, pathToTranslatedValue);
    if (isTranslationPresent) return get(obj, pathToTranslatedValue);

    return this.buildNonTranslatedReadableValue(objValue);
  }

  public buildNonTranslatedReadableValue(originalValue: string): string {
    if (!this.isNeedUserDefinedTranslate() || isEmpty(originalValue)) {
      return originalValue;
    }

    return isEmpty(originalValue)
      ? originalValue
      : `${originalValue} [${DEFAULT_LOCALE}]`;
  }

  public isNeedUserDefinedTranslate(): boolean {
    const { userLocale } = this.stateService.getDictionaryLocals();

    return !isEqual(DEFAULT_LOCALE, userLocale);
  }

  public revertUserDefinedTranslations<
    T extends IBaseEntityType | { items: IBaseEntityType[] },
  >(doc: T, identify: ServiceIdentifyType, i18nConfig?: II18nConfig): T {
    const defaultPathPrefix =
      has(doc, 'items') && isArray(doc?.items)
        ? ['items', ARRAY_OF_OBJECT_PATH_SEPARATOR]
        : '';

    return this.revertSimpleUserDefinedTranslations(
      doc,
      identify,
      merge({}, <II18nConfig>{ pathPrefix: defaultPathPrefix }, i18nConfig),
    );
  }

  public revertSimpleUserDefinedTranslations<T extends object = BaseEntityType>(
    doc: T,
    identify: ServiceIdentifyType,
    i18nConfig?: II18nConfig,
  ): T {
    const { locale, paths } = this.getUserDefinedConfigToProcess(
      identify,
      i18nConfig,
    );
    if (!locale || isEmpty(paths)) return doc;

    const clonedData = cloneDataWithDepth(doc, MAX_CLONE_DEPTH);

    return reduce(
      paths,
      (document: T, path: string): T => {
        const revertParams: UserDefinedTranslationRevertOptions<T> = {
          document,
          locale,
          path,
          skipNonExistedFields: !get(clonedData, 'is_master'),
        };

        const revertedResult = includes(path, ARRAY_OF_OBJECT_PATH_SEPARATOR)
          ? this.revertUserDefinedTranslationsForArrayOfObjects(revertParams)
          : this.revertUserDefinedTranslationsForSimpleField(revertParams);

        return revertedResult;
      },
      clonedData,
    );
  }

  private revertUserDefinedTranslationsForSimpleField<
    T extends object = BaseEntityType,
  >({
    document,
    locale,
    path,
    originalDocument,
    skipNonExistedFields,
  }: UserDefinedTranslationRevertOptions<T>): T {
    const { pathToField, fieldName, pathToStoreWithLocale } =
      this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
        path,
        locale,
      );
    const pathToOriginalValue = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      ORIGINAL_VALUE_FIELD,
      fieldName,
    ]);
    if (skipNonExistedFields && !has(document, path)) {
      unset(document, pathToOriginalValue);

      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }
    const documentWithOriginal =
      isEmpty(originalDocument) || has(document, pathToOriginalValue)
        ? document
        : originalDocument;

    const translatedValue = trim(get(document, path));
    const originalValue = get(documentWithOriginal, pathToOriginalValue);
    if (
      isEmpty(translatedValue) ||
      this.buildNonTranslatedReadableValue(originalValue) === translatedValue
    ) {
      unset(document, pathToStoreWithLocale);
    } else {
      set(document, pathToStoreWithLocale, translatedValue);
    }

    if (!has(documentWithOriginal, pathToOriginalValue)) {
      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }

    if (isNil(originalValue)) {
      unset(document, path);
      unset(document, pathToOriginalValue);

      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }

    unset(document, pathToOriginalValue);

    return set(
      this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      }),
      path,
      originalValue,
    );
  }

  private revertUserDefinedTranslationsForArrayOfObjects<
    T extends object = BaseEntityType,
  >({
    document,
    locale,
    path,
    skipNonExistedFields,
  }: UserDefinedTranslationRevertOptions<T>): T {
    const {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
      pathToStoreWithLocale,
      pathToFieldInObject,
    } = this.preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(
      path,
      locale,
    );

    const currentArrayValue = get(document, clearedPathToArray, []);
    if (isEmpty(currentArrayValue)) return document;
    const revertedArray = map(currentArrayValue, (currentObject, index) => {
      const clonedObject = cloneDataWithDepth(currentObject);

      if (!isEmpty(nestedPaths)) {
        // after decomposing pathsToTheCurrentField nestedPaths may have the structure like that
        // ['.nested_level_1.'] or ['.nested_level_1.', '.nested_level_2.']
        const clearedNestedPaths = chain(nestedPaths)
          .map((nestedPath) => trim(nestedPath, NESTED_FIELD_SEPARATOR))
          .join(
            `${NESTED_FIELD_SEPARATOR}${ARRAY_OF_OBJECT_PATH_SEPARATOR}${NESTED_FIELD_SEPARATOR}`,
          )
          .split(NESTED_FIELD_SEPARATOR)
          .compact()
          .value();

        const documentForRecursiveCall = get(document, [
          ...clearedPathToArray,
          `${index}`,
        ]);
        const newPathForRecursiveCall = [
          ...clearedNestedPaths,
          ARRAY_OF_OBJECT_PATH_SEPARATOR,
          ...clearedPathInObject,
        ].join(NESTED_FIELD_SEPARATOR);

        return this.revertUserDefinedTranslationsForArrayOfObjects({
          locale,
          document: documentForRecursiveCall,
          path: newPathForRecursiveCall,
        });
      }

      /**
       * will skip processing if the field to clear does not exist in the object
       * this is needed to avoid creating fields during revert process
       */
      if (skipNonExistedFields && !has(clonedObject, clearedPathInObject)) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      if (
        has(clonedObject, clearedPathInObject) &&
        !has(clonedObject, pathToStoreOriginalFieldValue)
      ) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      const translatedValue = trim(get(clonedObject, clearedPathInObject));

      if (isEmpty(translatedValue)) unset(clonedObject, pathToStoreWithLocale);
      else {
        if (has(clonedObject, pathToStoreWithLocale)) {
          set(clonedObject, pathToStoreWithLocale, translatedValue);
        }
      }
      if (!has(clonedObject, pathToStoreOriginalFieldValue)) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      const originalObjectValue = get(
        clonedObject,
        pathToStoreOriginalFieldValue,
      );
      unset(clonedObject, pathToStoreOriginalFieldValue);
      if (isNil(originalObjectValue)) unset(clonedObject, clearedPathInObject);
      else set(clonedObject, clearedPathInObject, originalObjectValue);

      return this.clearI18nObjectIfEmpty(clonedObject, {
        pathToField: pathToFieldInObject,
        pathsToClear: [locale],
      });
    });

    return set(document, clearedPathToArray, revertedArray);
  }

  private clearI18nObjectIfEmpty<T extends object>(
    obj: T,
    options?: {
      pathToField?: string[];
      unsetIfEmpty?: boolean;
      pathsToClear?: string[];
    },
  ): T {
    const { pathToField, unsetIfEmpty, pathsToClear } = merge(
      { pathToField: [], unsetIfEmpty: true, pathsToClear: [] },
      options,
    );

    const defaultClearPaths = [ORIGINAL_VALUE_FIELD];

    forEach(
      compact(uniq(concat(defaultClearPaths, pathsToClear))),
      (locale) => {
        const fieldToOriginalObject = [
          ...pathToField,
          TRANSLATION_OBJECT_FIELD,
          locale,
        ];
        const remainingTranslations = get(obj, fieldToOriginalObject, {});
        if (isEmpty(remainingTranslations)) unset(obj, fieldToOriginalObject);
      },
    );
    const pathToTranslationObject = [...pathToField, TRANSLATION_OBJECT_FIELD];
    if (
      has(obj, pathToTranslationObject) &&
      isEmpty(get(obj, pathToTranslationObject)) &&
      unsetIfEmpty
    ) {
      unset(obj, pathToTranslationObject);
    }

    return obj;
  }

  public removeI18nField<T extends object = object>(doc: T): T {
    if (isArray(doc)) {
      return map(doc, (item) => this.removeI18nField(item)) as T;
    } else if (isPlainObject(doc)) {
      return reduce(
        doc,
        (acc, value, key) => {
          if (isEqual(key, TRANSLATION_OBJECT_FIELD)) {
            return acc;
          }

          return {
            ...acc,
            [key]:
              isPlainObject(value) || isArray(value)
                ? this.removeI18nField(value as unknown as object)
                : value,
          };
        },
        {} as T,
      );
    }

    return doc;
  }
}
